/**
 * Registrations inbox — the queue of agencies asking to join.
 *
 * Not in the original design comp (which jumped straight to Onboard), so it is
 * built in the comp's language: filter tabs with live counts, a table, and a
 * detail drawer for the decision.
 *
 * Approving here provisions a real tenant and emails them to say so, so the
 * approve action is a deliberate two-step with the details shown for correction.
 */
import { useState } from 'react';
import {
  ArrowSquareOut,
  CheckCircle,
  MagnifyingGlass,
  Prohibit,
  Tray,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import {
  useApproveRegistration,
  useRegistrations,
  useRejectRegistration,
  useUpdateRegistration,
  type RegistrationFilters,
} from '../queries/platform';
import type { Registration, RegistrationStatus } from '../lib/types';
import { apiError } from '../lib/api';
import { relativeDate } from '../lib/format';
import {
  Avatar,
  Badge,
  DefinitionList,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  LoadingBlock,
  Modal,
  Segmented,
  SubmitButton,
  type Tone,
} from '../components/ui';

const STATUS_TONE: Record<RegistrationStatus, Tone> = {
  new: 'warn',
  reviewing: 'info',
  approved: 'ok',
  rejected: 'bad',
};
const STATUS_LABEL: Record<RegistrationStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  approved: 'Approved',
  rejected: 'Rejected',
};

const COLUMNS = '2.1fr 1.3fr 1fr .9fr 1.1fr';

export default function Registrations() {
  const [filters, setFilters] = useState<RegistrationFilters>({ status: 'new', search: '' });
  const [searchInput, setSearchInput] = useState('');
  /**
   * The open registration is held as a snapshot, not looked up in `rows` by id.
   * Approving moves it out of the "New" filter, so a derived lookup would
   * unmount the drawer the instant the list refetched — taking the confirmation
   * and the one-time invite link with it.
   */
  const [open, setOpen] = useState<Registration | null>(null);

  const { data, isPending, isError, error, refetch } = useRegistrations(filters);
  const rows = data?.data ?? [];
  const counts = data?.counts;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((f) => ({ ...f, search: searchInput.trim() }));
  };

  return (
    <div className="animate-up content-fill">
      <div className="card card-fill">
        <div className="card-head">
          <Segmented
            value={filters.status}
            onChange={(status) => setFilters((f) => ({ ...f, status }))}
            options={[
              { id: 'new', label: 'New', count: counts?.new },
              { id: 'reviewing', label: 'Reviewing', count: counts?.reviewing },
              { id: 'approved', label: 'Approved', count: counts?.approved },
              { id: 'rejected', label: 'Rejected', count: counts?.rejected },
              { id: 'all', label: 'All', count: counts?.all },
            ]}
          />
          <div className="spacer" />
          <form onSubmit={submitSearch} style={{ position: 'relative', width: 250 }}>
            <MagnifyingGlass
              size={15}
              color="var(--faint)"
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}
            />
            <input
              className="input"
              style={{ paddingLeft: 35 }}
              placeholder="Search agency, name, phone…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </form>
        </div>

        {isPending ? (
          <LoadingBlock label="Loading registrations…" />
        ) : isError ? (
          <ErrorState message={apiError(error)} onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Tray size={24} />}
            title={filters.search ? 'Nothing matches that search' : 'Nothing waiting'}
            message={
              filters.search
                ? 'Try a different agency name, contact, phone or email.'
                : filters.status === 'new'
                  ? 'New registrations from the app land here for review.'
                  : `No ${filters.status} registrations.`
            }
          />
        ) : (
          <div className="table-scroll">
            <div>
              <div
                className="table-head"
                style={{ gridTemplateColumns: COLUMNS, position: 'sticky', top: 0, zIndex: 1 }}
              >
                <span>Agency</span>
                <span>Contact</span>
                <span>Status</span>
                <span>Received</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </div>

              {rows.map((registration) => (
                <div
                  key={registration._id}
                  className="table-row"
                  style={{ gridTemplateColumns: COLUMNS }}
                >
                  <button
                    onClick={() => setOpen(registration)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      minWidth: 0,
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <Avatar name={registration.agencyName} />
                    <div style={{ minWidth: 0 }}>
                      <p className="truncate strong" style={{ margin: 0, fontSize: 13.5 }}>
                        {registration.agencyName}
                      </p>
                      <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
                        {registration.city || 'City not given'}
                      </p>
                    </div>
                  </button>

                  <div style={{ minWidth: 0 }}>
                    <p className="truncate t-md" style={{ margin: 0 }}>
                      {registration.contactName}
                    </p>
                    <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
                      {registration.phone}
                    </p>
                  </div>

                  <div>
                    <Badge tone={STATUS_TONE[registration.status]}>
                      {STATUS_LABEL[registration.status]}
                    </Badge>
                  </div>

                  <span className="t-sm muted">{relativeDate(registration.createdAt)}</span>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
                    <button className="btn btn-sm" onClick={() => setOpen(registration)}>
                      Review
                    </button>
                  </div>
                </div>
              ))}

            </div>
          </div>
        )}

        {!isPending && !isError && rows.length > 0 && (
          <div className="table-foot">
            <p className="t-sm muted" style={{ margin: 0 }}>
              Showing {rows.length}
              {counts && filters.status !== 'all' ? ` of ${counts.all} total` : ''}
            </p>
          </div>
        )}
      </div>

      {open && <RegistrationDrawer key={open._id} registration={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function RegistrationDrawer({
  registration,
  onClose,
}: {
  registration: Registration;
  onClose: () => void;
}) {
  const [notes, setNotes] = useState(registration.notes ?? '');
  const [confirming, setConfirming] = useState<'approve' | 'reject' | null>(null);
  const [result, setResult] = useState<{ message: string } | null>(null);
  const [error, setError] = useState('');

  const updating = useUpdateRegistration();
  const approving = useApproveRegistration();
  const rejecting = useRejectRegistration();

  const decided = registration.status === 'approved' || registration.status === 'rejected';
  const agency =
    registration.agency && typeof registration.agency === 'object' ? registration.agency : null;
  const reviewer =
    registration.reviewedBy && typeof registration.reviewedBy === 'object'
      ? registration.reviewedBy
      : null;

  const saveNotes = async () => {
    setError('');
    try {
      await updating.mutateAsync({ id: registration._id, notes });
    } catch (err) {
      setError(apiError(err));
    }
  };

  const markReviewing = async () => {
    setError('');
    try {
      await updating.mutateAsync({ id: registration._id, status: 'reviewing', notes });
    } catch (err) {
      setError(apiError(err));
    }
  };

  if (result) {
    return (
      <Drawer
        title="Agency created"
        subtitle={registration.agencyName}
        onClose={onClose}
        footer={
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <div style={{ textAlign: 'center', padding: '18px 0 8px' }}>
          <CheckCircle size={44} color="var(--ok)" weight="fill" />
          <h3 style={{ fontSize: 17, margin: '12px 0 6px' }}>{result.message}</h3>
          <p className="t-sm muted" style={{ margin: 0, lineHeight: 1.6 }}>
            They sign in with that mobile number, and we text a code to it each time.
          </p>
        </div>

        {/* Only present outside production, where no mailbox may be wired up. */}
      </Drawer>
    );
  }

  return (
    <>
      <Drawer
        title={registration.agencyName}
        subtitle={`Registered ${relativeDate(registration.createdAt)}`}
        onClose={onClose}
        footer={
          decided ? (
            <>
              <Badge tone={STATUS_TONE[registration.status]}>
                {STATUS_LABEL[registration.status]}
              </Badge>
              <div className="spacer" />
              {agency && (
                <Link className="btn" to={`/agencies/${agency._id}`}>
                  <ArrowSquareOut size={15} /> Open agency
                </Link>
              )}
            </>
          ) : (
            <>
              <button className="btn btn-ok" onClick={() => setConfirming('approve')}>
                <CheckCircle size={15} /> Approve &amp; onboard
              </button>
              <button className="btn btn-bad" onClick={() => setConfirming('reject')}>
                <Prohibit size={15} /> Reject
              </button>
              <div className="spacer" />
              {registration.status === 'new' && (
                <button className="btn" onClick={markReviewing} disabled={updating.isPending}>
                  Mark reviewing
                </button>
              )}
            </>
          )
        }
      >
        <div className="stack" style={{ gap: 18 }}>
          <div>
            <p className="t-xs strong muted" style={{ margin: '0 0 10px', letterSpacing: 0.5 }}>
              SUBMITTED DETAILS
            </p>
            <DefinitionList
              rows={[
                ['Agency', registration.agencyName],
                ['Contact', registration.contactName],
                ['Mobile', registration.phone],
                ['Email', registration.email],
                ['City', registration.city],
                ['Website', registration.website],
                ['Address', registration.address],
                ['Message', registration.message],
              ]}
            />
          </div>

          {decided && (
            <div>
              <p className="t-xs strong muted" style={{ margin: '0 0 10px', letterSpacing: 0.5 }}>
                DECISION
              </p>
              <DefinitionList
                rows={[
                  ['Status', STATUS_LABEL[registration.status]],
                  ['By', reviewer?.name],
                  ['When', relativeDate(registration.reviewedAt)],
                  ['Reason', registration.rejectionReason],
                ]}
              />
            </div>
          )}

          <div>
            <Field label="Internal notes" hint="Only visible in this console.">
              <textarea
                className="textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Called them, sounds legitimate…"
                disabled={decided}
              />
            </Field>
            {!decided && (
              <button
                className="btn btn-sm"
                style={{ marginTop: 9 }}
                onClick={saveNotes}
                disabled={updating.isPending || notes === (registration.notes ?? '')}
              >
                Save notes
              </button>
            )}
          </div>

          {error && <p className="field-error">{error}</p>}
        </div>
      </Drawer>

      {confirming === 'approve' && (
        <ApproveModal
          registration={registration}
          pending={approving.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={async (overrides) => {
            setError('');
            try {
              const response = await approving.mutateAsync({
                id: registration._id,
                notes,
                ...overrides,
              });
              setConfirming(null);
              setResult({ message: response.message });
            } catch (err) {
              setError(apiError(err));
              setConfirming(null);
            }
          }}
        />
      )}

      {confirming === 'reject' && (
        <RejectModal
          agencyName={registration.agencyName}
          pending={rejecting.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={async (reason, notify) => {
            setError('');
            try {
              await rejecting.mutateAsync({ id: registration._id, reason, notify });
              setConfirming(null);
              onClose();
            } catch (err) {
              setError(apiError(err));
              setConfirming(null);
            }
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

interface Overrides {
  agencyName: string;
  contactName: string;
  phone: string;
  email: string;
}

function ApproveModal({
  registration,
  pending,
  onCancel,
  onConfirm,
}: {
  registration: Registration;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (overrides: Overrides) => void;
}) {
  // Editable because these values become the tenant and the invite recipient —
  // a typo'd email sends the only way in to nobody.
  const [form, setForm] = useState<Overrides>({
    agencyName: registration.agencyName,
    contactName: registration.contactName,
    phone: registration.phone,
    email: registration.email,
  });

  const set = (key: keyof Overrides, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <Modal
      title="Approve and onboard"
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <SubmitButton
            pending={pending}
            pendingLabel="Creating…"
            className="btn btn-primary"
            onClick={() => onConfirm(form)}
          >
            Create agency &amp; send invite
          </SubmitButton>
        </>
      }
    >
      <p className="t-sm muted" style={{ margin: '0 0 16px', lineHeight: 1.6 }}>
        This creates the tenant and emails a single-use link where they choose their own 4-digit
        Check the details: the mobile number is the only way they get in.
      </p>

      <div className="stack" style={{ gap: 12 }}>
        <Field label="Agency name" required>
          <input
            className="input"
            value={form.agencyName}
            onChange={(e) => set('agencyName', e.target.value)}
          />
        </Field>
        <Field label="Contact name" required>
          <input
            className="input"
            value={form.contactName}
            onChange={(e) => set('contactName', e.target.value)}
          />
        </Field>
        <Field label="Mobile number" hint="What they will sign in with." required>
          <input
            className="input"
            inputMode="numeric"
            maxLength={10}
            value={form.phone}
            onChange={(e) => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
          />
        </Field>
        <Field label="Email" hint="Where we tell them the workspace is ready." required>
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

function RejectModal({
  agencyName,
  pending,
  onCancel,
  onConfirm,
}: {
  agencyName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string, notify: boolean) => void;
}) {
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);

  return (
    <Modal
      title={`Reject ${agencyName}?`}
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <SubmitButton
            pending={pending}
            pendingLabel="Rejecting…"
            className="btn btn-bad"
            onClick={() => onConfirm(reason.trim(), notify)}
          >
            Reject registration
          </SubmitButton>
        </>
      }
    >
      <p className="t-sm muted" style={{ margin: '0 0 16px', lineHeight: 1.6 }}>
        No account is created. This can&apos;t be undone, and they would need to register again.
      </p>

      <Field
        label="Reason"
        hint="Included in the email, so keep it something you'd be happy for them to read."
      >
        <textarea
          className="textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="We could not verify the agency."
        />
      </Field>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          marginTop: 14,
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        Email them about this decision
      </label>
    </Modal>
  );
}
